import { useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import useAdminQuery from "../../lib/queries/useAdminQuery";
import { Topbar } from "../../components/AppShell";
import { FiltersBar, filtersToPills } from "../../lib/funnel/filters";
import type { Filters } from "../../lib/funnel/filters";
import type { FilterPillSpec } from "../../components/FilterPills";
import { EmptyState } from "../../lib/funnel/charts";
import { previousMonth } from "../../lib/funnel/helpers";
import { QueryInspector } from "../../lib/funnel/QueryInspector";
import { InfoButton } from "../../lib/funnel/InfoButton";
import { DEMO_FUNNEL_SQL, LEAD_CAL_SUPPLEMENTARY_SQL, mapDemoAttendeeRows, mapLeadCalRows } from "./sql";
import type { DemoAttendeeRow, LeadCalRow } from "./sql";
import { applyDemoFilters, applyDemoCohortFilters, applyLeadCalFilters, groupCountryFallback, collapseToCompany, companyKey, companyDisplayName } from "./helpers";
import type { ViewMode } from "./helpers";
import { PRIMARY, MUTED, FG1 } from "../../lib/funnel/theme";
import { KpiRow } from "./KpiRow";
import { DemoFunnelButterfly } from "./DemoFunnelButterfly";
import { DemoFunnelColumnLists } from "./FunnelColumnLists";
import { DemoDrilldownTable } from "./DemoDrilldownTable";
import { DemoFunnelExplain, DemoFunnelSchema } from "./explainBlocks";
import { LoadingPanel } from "../../components/LoadingPanel";
import { GeoSplit } from "./panels/GeoSplit";
import { SignupTimingChart } from "./panels/SignupTimingChart";
import { DaysToSignupHistogram } from "./panels/DaysToSignupHistogram";
import { CompetitorMentionsPanel, IntegrationMentionsPanel, PainPointsPanel, ObjectionsPanel } from "./panels/MentionPanels";
import { DemoerBreakdown } from "./panels/DemoerBreakdown";
import { LeadCalSupplementary } from "./panels/LeadCalSupplementary";

const CLEAN_FROM = "2026-03";

function ViewModeToggle({ viewMode, onChange }: { viewMode: ViewMode; onChange: (v: ViewMode) => void }) {
  const opts: { key: ViewMode; label: string }[] = [
    { key: "company", label: "Company" },
    { key: "attendee", label: "Attendee" },
  ];
  return (
    <div role="group" aria-label="View mode" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 11, color: MUTED, textTransform: "uppercase", letterSpacing: 0.5 }}>View</span>
      <div style={{ display: "inline-flex", border: `1px solid ${MUTED}`, borderRadius: 6, overflow: "hidden", fontSize: 12 }}>
        {opts.map((opt) => {
          const active = viewMode === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => onChange(opt.key)}
              style={{
                padding: "4px 12px",
                background: active ? PRIMARY : "transparent",
                color: active ? "#fff" : FG1,
                border: "none", cursor: "pointer", fontWeight: active ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function DemoFunnel() {
  const { pathname } = useLocation();
  const showDrilldownTable = pathname === "/demo-funnel-secret";
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [splitPaid, setSplitPaid] = useState(false);
  const [highlightedCompanyKey, setHighlightedCompanyKey] = useState<string | null>(null);
  const [highlightedAttendeeKey, setHighlightedAttendeeKey] = useState<string | null>(null);
  const clearSelection = () => {
    setHighlightedCompanyKey(null);
    setHighlightedAttendeeKey(null);
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode: ViewMode = searchParams.get("view") === "attendee" ? "attendee" : "company";
  const setViewMode = (next: ViewMode) => {
    const sp = new URLSearchParams(searchParams);
    if (next === "attendee") sp.set("view", "attendee");
    else sp.delete("view");
    setSearchParams(sp, { replace: true });
  };
  const [filters, setFilters] = useState<Filters>(() => ({
    months: [previousMonth()],
    countries: [],
    roles: [],
    companySizes: [],
    excludeSupport: true,
    granularity: "month",
    cohortDayFrom: null,
    cohortDayTo: null,
    demoBucketing: "first_demo_date",
    demoTypes: ["getting_started"],
  }));

  const demoQuery = useAdminQuery(DEMO_FUNNEL_SQL);
  const leadCalQuery = useAdminQuery(LEAD_CAL_SUPPLEMENTARY_SQL);

  const rawDemoRows = useMemo<DemoAttendeeRow[]>(() => {
    if (!demoQuery.data) return [];
    return mapDemoAttendeeRows(demoQuery.data.columns, demoQuery.data.rows as unknown[][]);
  }, [demoQuery.data]);

  const rawLeadCalRows = useMemo<LeadCalRow[]>(() => {
    if (!leadCalQuery.data) return [];
    return mapLeadCalRows(leadCalQuery.data.columns, leadCalQuery.data.rows as unknown[][]);
  }, [leadCalQuery.data]);

  const availableCountries = useMemo(() => {
    const set = new Set<string>();
    rawDemoRows.forEach((r) => set.add(groupCountryFallback(r)));
    return Array.from(set);
  }, [rawDemoRows]);

  const availableRoles = useMemo(() => {
    const set = new Set<string>();
    rawDemoRows.forEach((r) => { if (r.user_role) set.add(r.user_role); });
    return Array.from(set).sort();
  }, [rawDemoRows]);

  const availableDemoTypes = useMemo(() => {
    const set = new Set<string>();
    rawDemoRows.forEach((r) => { if (r.demo_type) set.add(r.demo_type); });
    return Array.from(set).sort();
  }, [rawDemoRows]);

  const availableDemoers = useMemo(() => {
    if (!showDrilldownTable) return undefined;
    const set = new Set<string>();
    rawDemoRows.forEach((r) => set.add(r.organiser_email ?? "(unknown)"));
    return Array.from(set).sort();
  }, [rawDemoRows, showDrilldownTable]);

  const cohortDemoRows = useMemo(() => applyDemoCohortFilters(rawDemoRows, filters), [rawDemoRows, filters]);
  const filteredDemoRows = useMemo(() => applyDemoFilters(rawDemoRows, filters), [rawDemoRows, filters]);
  // Demoer breakdown ignores the demoer filter so all demoers stay visible
  // and the active selection reads as a highlight rather than a 1-row table.
  const demoerBreakdownRows = useMemo(
    () => applyDemoFilters(rawDemoRows, { ...filters, demoers: [] }),
    [rawDemoRows, filters],
  );
  const filteredLeadCalRows = useMemo(() => applyLeadCalFilters(rawLeadCalRows, filters), [rawLeadCalRows, filters]);

  const pagePills = useMemo<FilterPillSpec[]>(() => {
    const base = filtersToPills(filters);
    base.push({
      kind: "mode",
      label: viewMode === "company" ? "Company view" : "Attendee view",
    });
    return base;
  }, [filters, viewMode]);

  // baseRows is what every panel above the drilldown table sees: attendee-level
  // rows in attendee mode, company-collapsed rows in company mode. The
  // drilldown table always reads filteredDemoRows so per-attendee detail is
  // preserved there.
  const baseRows = useMemo(
    () => (viewMode === "company" ? collapseToCompany(filteredDemoRows) : filteredDemoRows),
    [filteredDemoRows, viewMode],
  );

  // Mention panels filter to the selected entity's rows when a company or
  // attendee is pinned. Attendee selection is the more specific filter; in
  // company mode it still maps to company-level rows since the page rolls up.
  const mentionRows = useMemo(() => {
    if (highlightedAttendeeKey) {
      return filteredDemoRows.filter((r) => r.attendee_email === highlightedAttendeeKey);
    }
    if (highlightedCompanyKey) {
      return baseRows.filter((r) => companyKey(r) === highlightedCompanyKey);
    }
    return baseRows;
  }, [baseRows, filteredDemoRows, highlightedCompanyKey, highlightedAttendeeKey]);

  const selectionLabel = useMemo<string | null>(() => {
    if (highlightedAttendeeKey) {
      const r = filteredDemoRows.find((x) => x.attendee_email === highlightedAttendeeKey);
      if (!r) return highlightedAttendeeKey;
      const name = (r.user_full_name ?? "").trim();
      return name ? `${name} (${r.attendee_email})` : r.attendee_email;
    }
    if (highlightedCompanyKey) {
      const r = filteredDemoRows.find((x) => companyKey(x) === highlightedCompanyKey)
        ?? baseRows.find((x) => companyKey(x) === highlightedCompanyKey);
      return r ? companyDisplayName(r) : highlightedCompanyKey;
    }
    return null;
  }, [highlightedCompanyKey, highlightedAttendeeKey, filteredDemoRows, baseRows]);

  const includesBackfilled = useMemo(() => {
    if (filters.granularity === "day") {
      const from = filters.cohortDayFrom;
      return !!from && from.slice(0, 7) < CLEAN_FROM;
    }
    if (filters.months.length === 0) return true;
    return filters.months.some((m) => m < CLEAN_FROM);
  }, [filters.months, filters.granularity, filters.cohortDayFrom]);

  return (
    <>
      <Topbar crumbs={["Dashboards", "Demo Conversion"]} />
      <div className="page">
        <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <h1 style={{ margin: 0 }}>Demo Conversion</h1>
              {showDrilldownTable && (
                <span style={{
                  fontSize: 11, fontWeight: 600, color: "#b91c1c",
                  background: "#fee2e2", border: "1px dashed #b91c1c",
                  borderRadius: 4, padding: "2px 7px", letterSpacing: "0.04em",
                }}>
                  secret
                </span>
              )}
              <button
                className="schema-info-btn"
                onClick={() => setInspectorOpen(true)}
                title="Inspect query"
                aria-label="Inspect query"
              >
                <i className="ti ti-info-circle" />
              </button>
            </div>
            <p className="lede">
              Of demos we run, who they're with, where they came from, and how they convert through to paid.
              Spine is reporting.demo (is_demo=true); {viewMode === "company" ? "rolled up to one prospect company per matched workspace (or per demo when unmatched)." : "one row per attendee."}
            </p>
          </div>
          <ViewModeToggle viewMode={viewMode} onChange={setViewMode} />
        </div>

        {inspectorOpen && (
          <QueryInspector
            title="Demo Conversion query inspector"
            open={inspectorOpen}
            onClose={() => setInspectorOpen(false)}
            schemaContent={<DemoFunnelSchema />}
            explainContent={<DemoFunnelExplain />}
            queryResult={demoQuery.data ? { columns: demoQuery.data.columns, rows: demoQuery.data.rows as unknown[][], rowCount: demoQuery.data.rowCount } : null}
            csvFilenamePrefix="demo-funnel-raw"
            filters={filters}
            sqlQueries={[
              { label: "Main funnel query", sql: DEMO_FUNNEL_SQL },
              { label: "Lead_cal supplementary", sql: LEAD_CAL_SUPPLEMENTARY_SQL },
            ]}
          />
        )}

        <FiltersBar
          filters={filters}
          onChange={setFilters}
          availableCountries={availableCountries}
          availableRoles={availableRoles}
          availableDemoTypes={availableDemoTypes}
          availableDemoers={availableDemoers}
          usersInView={filteredDemoRows.length}
          cohortVariant="single-with-day"
        />

        {includesBackfilled && (
          <div style={{
            margin: "8px 0",
            padding: "8px 12px",
            background: "var(--bg-warning, #FFF7E5)",
            border: "1px solid var(--border-default)",
            borderRadius: 6,
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
            <strong>Historical demo data.</strong>
            <InfoButton width={420}>
              <p style={{ margin: 0 }}>
                Pre-Mar 2026 numbers come from a one-off historical append, not the live pipeline.
              </p>
              <ul style={{ margin: "8px 0 0 0", paddingLeft: 18 }}>
                <li>
                  <code>reporting.demo</code> is organic from <strong>26 Jan 2026 05:09 UTC</strong>; everything up to <strong>25 Jan 23:59 UTC</strong> was appended in bulk. Demo metadata (lead status, mentions, timeline urgency, etc.) is reconstructed from transcripts and may be sparser than live demos.
                </li>
                <li style={{ marginTop: 6 }}>
                  <code>reporting.lead_cal</code> is organic from <strong>26 Feb 2026 21:36 UTC</strong>; bookings up to <strong>26 Feb 21:30 UTC</strong> were appended in bulk. <code>demo_attended</code> is true for pre-25-Jan-2026 bookings, but bookings in <strong>25 Jan – 26 Feb 2026</strong> have <code>demo_attended=false</code> until a follow-up backfill lands. Booking → demo linkage in that window is currently understated.
                </li>
              </ul>
            </InfoButton>
            <span style={{ color: "var(--fg-2)" }}>
              Limited data before Feb 2026: expect gaps in transcript-derived fields and lower confidence on who actually showed.
            </span>
          </div>
        )}

        {demoQuery.isLoading || leadCalQuery.isLoading ? (
          <LoadingPanel />
        ) : demoQuery.isError ? (
          <p style={{ color: "var(--fg-danger)" }}>{(demoQuery.error as Error).message}</p>
        ) : leadCalQuery.isError ? (
          <p style={{ color: "var(--fg-danger)" }}>{(leadCalQuery.error as Error).message}</p>
        ) : rawDemoRows.length === 0 ? (
          <EmptyState message="No demos found in the source query." />
        ) : filteredDemoRows.length === 0 ? (
          <EmptyState message="No demos match this filter combination. Try widening the cohort." />
        ) : (
          <>
            <KpiRow demoRows={baseRows} cohortDemoRows={cohortDemoRows} leadCalRows={filteredLeadCalRows} viewMode={viewMode} />
            <DemoFunnelButterfly
              rows={baseRows}
              splitPaid={splitPaid}
              onSplitPaidChange={setSplitPaid}
              viewMode={viewMode}
              pagePills={pagePills}
            />
            <DemoFunnelColumnLists
              rows={filteredDemoRows}
              splitPaid={splitPaid}
              viewMode={viewMode}
              highlightedCompanyKey={highlightedCompanyKey}
              onHighlightCompany={(k) => { setHighlightedCompanyKey(k); if (k == null) setHighlightedAttendeeKey(null); }}
              highlightedAttendeeKey={highlightedAttendeeKey}
              onHighlightAttendee={setHighlightedAttendeeKey}
              pagePills={pagePills}
              selectionLabel={selectionLabel}
              onClearSelection={clearSelection}
            />
            <SignupTimingChart rows={baseRows} viewMode={viewMode} pagePills={pagePills} />
            <GeoSplit rows={baseRows} viewMode={viewMode} pagePills={pagePills} />
            <div className="chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ gridColumn: "span 2" }}>
                <DaysToSignupHistogram rows={baseRows} viewMode={viewMode} pagePills={pagePills} />
              </div>
              <CompetitorMentionsPanel rows={mentionRows} viewMode={viewMode} selectionLabel={selectionLabel} onClearSelection={clearSelection} pagePills={pagePills} />
              <IntegrationMentionsPanel rows={mentionRows} viewMode={viewMode} selectionLabel={selectionLabel} onClearSelection={clearSelection} pagePills={pagePills} />
              <PainPointsPanel rows={mentionRows} viewMode={viewMode} selectionLabel={selectionLabel} onClearSelection={clearSelection} pagePills={pagePills} />
              <ObjectionsPanel rows={mentionRows} viewMode={viewMode} selectionLabel={selectionLabel} onClearSelection={clearSelection} pagePills={pagePills} />
            </div>
            {showDrilldownTable && (
              <DemoerBreakdown
                rows={demoerBreakdownRows}
                viewMode={viewMode}
                selectedDemoers={filters.demoers ?? []}
                onToggleDemoer={(demoer) => {
                  const cur = filters.demoers ?? [];
                  const next = cur.includes(demoer) ? cur.filter((d) => d !== demoer) : [...cur, demoer];
                  setFilters({ ...filters, demoers: next });
                }}
                pagePills={pagePills}
              />
            )}
            <DemoDrilldownTable
              rows={filteredDemoRows}
              filters={filters}
              viewMode={viewMode}
              highlightedCompanyKey={highlightedCompanyKey}
              onHighlightCompany={setHighlightedCompanyKey}
              pagePills={pagePills}
            />
            <LeadCalSupplementary demoRows={filteredDemoRows} leadCalRows={filteredLeadCalRows} pagePills={pagePills} />
          </>
        )}
      </div>
    </>
  );
}
